import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, UserCheck, UserX, Mail, Phone, MapPin, FileText, Users, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { roleService } from '../services/roleService';
import { User, CreateUserForm, Role } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import DataGrid from '../components/grid/DataGrid';
import Modal from '../components/ui/Modal';
import UserForm from '../components/forms/UserForm';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import UserAuditModal from '../components/modals/UserAuditModal';
import { formatPhoneNumber } from '../utils/formatters';

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { usageStats, subscription, refreshSubscription } = useSubscription();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showToggleDialog, setShowToggleDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formError, setFormError] = useState('');
  const [showUserAuditModal, setShowUserAuditModal] = useState(false);
  const [selectedUserForAudit, setSelectedUserForAudit] = useState<User | null>(null);
  const pageSize = 10;

  // Check if user limit is reached
  const currentUserCount = usageStats?.currentUsers || 0;
  const maxUsers = usageStats?.maxUsers || -1; // -1 means unlimited
  const isUserLimitReached = maxUsers !== -1 && currentUserCount >= maxUsers;
  const planName = subscription?.planName || 'your plan';

  // Fetch users
  const {
    data: usersResponse,
    isLoading: usersLoading,
    error: usersError
  } = useQuery({
    queryKey: ['users', page, pageSize, statusFilter, search],
    queryFn: () => apiService.getUsers(page, pageSize, statusFilter, search),
    retry: 1,
    retryDelay: 1000
  });

  // Fetch roles for dropdowns
  const {
    data: rolesResponse
  } = useQuery({
    queryKey: ['roles'],
    queryFn: roleService.getRoles
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserForm) => apiService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      refreshSubscription();
      setShowCreateModal(false);
      toast.success('User created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Omit<CreateUserForm, 'password'> }) => 
      apiService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      refreshSubscription();
      setShowEditModal(false);
      setSelectedUser(null);
      toast.success('User updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      refreshSubscription();
      setShowDeleteDialog(false);
      setSelectedUser(null);
      toast.success('User deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  });

  const toggleUserStatusMutation = useMutation({
    mutationFn: (id: string) => apiService.toggleUserActiveStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      refreshSubscription();
      const wasActive = selectedUser?.isActive;
      setShowToggleDialog(false);
      setSelectedUser(null);
      toast.success(wasActive ? 'User deactivated successfully' : 'User activated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
  });

  // Handlers
  const handleCreate = (data: CreateUserForm) => {
    createUserMutation.mutate(data);
  };

  const handleEdit = useCallback((user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
    setFormError('');
  }, []);

  const handleUpdate = (data: Omit<CreateUserForm, 'password'>) => {
    if (selectedUser) {
      updateUserMutation.mutate({ id: selectedUser.id, data });
    }
  };

  const handleDelete = useCallback((user: User) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  }, []);

  const confirmDelete = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  const handleToggleStatus = useCallback((user: User) => {
    setSelectedUser(user);
    setShowToggleDialog(true);
  }, []);

  const confirmToggleStatus = () => {
    if (selectedUser) {
      toggleUserStatusMutation.mutate(selectedUser.id);
    }
  };

  const handleSearch = (term: string) => {
    setSearch(term);
    setPage(1); // Reset to first page when searching
  };

  const handleViewAuditLogs = useCallback((user: User) => {
    setSelectedUserForAudit(user);
    setShowUserAuditModal(true);
  }, []);

  const users = usersResponse?.data?.data || [];
  const totalCount = usersResponse?.data?.totalCount || 0;
  const totalPages = usersResponse?.data?.totalPages || Math.max(1, Math.ceil(totalCount / pageSize));

  // Define columns for DataGrid
  const columns = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (value: any, row: User) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{row.firstName} {row.lastName}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{row.email}</div>
        </div>
      )
    },
    {
      id: 'contact',
      header: 'Contact Info',
      sortable: true,
          cell: (value: any, row: User) => (
            <div className="space-y-1">
              <div className="flex items-center space-x-1">
                <Mail className="h-3 w-3 text-gray-400" />
                <a
                  href={`mailto:${row.email}`}
                  className="text-accent-color dark:text-accent-color-400 hover:text-accent-color-800 dark:hover:text-accent-color-300 hover:underline text-sm"
                >
                  {row.email}
                </a>
              </div>
              {row.phone && (
                <div className="flex items-center space-x-1">
                  <Phone className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">{formatPhoneNumber(row.phone)}</span>
                </div>
              )}
              {(row.city || row.state || row.zipCode) && (
                <div className="flex items-center space-x-1">
                  <MapPin className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {[row.city, row.state, row.zipCode].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          )
    },
    {
      id: 'role',
      header: 'Role',
      sortable: true,
      cell: (value: any, row: User) => {
        const getRoleDisplay = (roleName: string) => {
          switch (roleName) {
            case 'SuperAdmin': return 'bg-purple-100 text-purple-800';
            case 'Admin': return 'bg-red-100 text-red-800';
            case 'User': return 'bg-accent-color-100 text-accent-color-800';
            case 'Viewer': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
          }
        };
        
        // Find the role by ID from the fetched roles
        const role = rolesResponse?.data?.find(r => r.id === row.roleId);
        const roleName = role?.name || 'Unknown';
        const roleClass = getRoleDisplay(roleName);
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleClass}`}>
            {roleName}
          </span>
        );
      }
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cell: (value: any, row: User) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.isActive 
            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      id: 'createdDate',
      header: 'Created',
      sortable: true,
      cell: (value: string, row: User) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <div className="text-gray-900 dark:text-gray-100">{new Date(value).toLocaleDateString()}</div>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            <button 
              onClick={() => handleEdit(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit User"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => handleViewAuditLogs(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="View Logs"
            >
              <FileText className="h-4 w-4"/>
            </button>
            {row.id !== currentUser?.id && (
              <button 
                onClick={() => handleToggleStatus(row)}
                className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                title={row.isActive ? 'Deactivate User' : 'Activate User'}
                aria-label={row.isActive ? 'Deactivate User' : 'Activate User'}
              >
                {row.isActive ? <UserX className="h-4 w-4"/> : <UserCheck className="h-4 w-4"/>}
              </button>
            )}
            {row.id !== currentUser?.id && (
              <button 
                onClick={() => handleDelete(row)}
                className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
                title="Delete User"
              >
                <Trash2 className="h-4 w-4"/>
              </button>
            )}
          </div>
        </div>
      )
    }
  ], [handleEdit, handleDelete, handleToggleStatus, handleViewAuditLogs, currentUser?.id, rolesResponse?.data]);

  // Error handling
  if (usersError) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-red-600 dark:text-red-400 text-lg font-medium">Error loading users</div>
          <div className="text-gray-500 dark:text-gray-400 mt-2">Please try again</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Users className="h-8 w-8 text-gray-600 dark:text-gray-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Users</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage user accounts and permissions
              {maxUsers !== -1 && (
                <span className="ml-2 text-xs">
                  ({currentUserCount}/{maxUsers} users)
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label htmlFor="user-status-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Status
          </label>
          <select
            id="user-status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
              setPage(1);
            }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
            aria-label="Filter users by status"
          >
            <option value="all">All Users</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
          <div className="relative group">
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={isUserLimitReached}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={isUserLimitReached ? `User limit reached for ${planName}` : ''}
            >
              {isUserLimitReached && <Lock className="h-4 w-4" />}
              <Plus className="h-4 w-4" />
              <span>Add User</span>
            </button>
            {isUserLimitReached && (
              <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10">
                <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap shadow-lg">
                  User limit reached ({maxUsers} users). Upgrade to add more.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Limit Warning Banner */}
      {isUserLimitReached && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-1">
                User Limit Reached
              </h3>
              <p className="text-sm text-orange-700 dark:text-orange-400">
                You've reached the maximum of {maxUsers} users allowed on the {planName} plan.
                To add more users, please upgrade your subscription.
              </p>
              <a
                href="/settings/subscription"
                className="inline-flex items-center mt-2 text-sm font-medium text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300"
              >
                View subscription plans →
              </a>
            </div>
          </div>
        </div>
      )}

      <DataGrid
        data={users}
        columns={columns}
        searchTerm={search}
        onSearch={handleSearch}
        currentPage={page}
        onPageChange={setPage}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        isLoading={usersLoading}
        searchPlaceholder="Search users by name or email..."
        onRowClick={(row) => handleEdit(row as User)}
        rowTooltip="Edit User"
        emptyMessage={
          search
            ? 'No users match your search'
            : statusFilter === 'inactive'
              ? 'No inactive users'
              : statusFilter === 'active'
                ? 'No active users'
                : 'No users found'
        }
        getRowClassName={(row) => (row as User).isActive ? 'group' : 'group opacity-70'}
      />

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New User"
        maxWidth="xl"
      >
        <UserForm
          roles={rolesResponse?.data || []}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          isLoading={createUserMutation.isPending}
          error={formError}
        />
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        title="Edit User"
        maxWidth="xl"
      >
        {selectedUser && (
          <UserForm
            user={selectedUser}
            roles={rolesResponse?.data || []}
            onSubmit={handleUpdate}
            onCancel={() => setShowEditModal(false)}
            isLoading={updateUserMutation.isPending}
            error={formError}
            canDeactivate={selectedUser.id !== currentUser?.id}
          />
        )}
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete ${selectedUser?.firstName} ${selectedUser?.lastName}? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteUserMutation.isPending}
      />

      <ConfirmDialog
        isOpen={showToggleDialog}
        onClose={() => {
          setShowToggleDialog(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmToggleStatus}
        title={selectedUser?.isActive ? 'Deactivate User' : 'Activate User'}
        message={selectedUser?.isActive
          ? `Deactivate ${selectedUser.firstName} ${selectedUser.lastName}? They will not be able to log in until reactivated.`
          : `Activate ${selectedUser?.firstName} ${selectedUser?.lastName}? They will be able to log in again.`}
        confirmText={selectedUser?.isActive ? 'Deactivate' : 'Activate'}
        variant={selectedUser?.isActive ? 'warning' : 'info'}
        isLoading={toggleUserStatusMutation.isPending}
      />

      <UserAuditModal
        user={selectedUserForAudit}
        isOpen={showUserAuditModal}
        onClose={() => {
          setShowUserAuditModal(false);
          setSelectedUserForAudit(null);
        }}
      />
    </div>
  );
};

export default UsersPage;
